import type { PremiumCorrection } from "@/types/simpego-api";
"use client";

import { BaseCoverageId } from "@/config/coverages";

export interface SerializedFunnelData {
  contractId?: string | null;
  contractToken?: string | null;
  offerAgentId?: string | null;
  offerWhitelabelPartner?: string | null;
  petType?: "cat" | "dog" | null;
  petName?: string | null;
  breed?: string | null;
  breedLabel?: string | null;
  birthDate?: string | null;
  selectedBreedType?: "purebred" | "crossbreed" | "mixed" | null;
  gender?: "male" | "female" | "unknown" | null;
  isCastrated?: boolean;
  isHouseCat?: boolean;
  selectedDogSize?: "small" | "large" | null;
  chipNumber?: string | null;
  preexistingConditionCategories?: string[];
  preexistingConditionComment?: string | null;
  bundlePrices?: { S: number; M: number; L: number } | null;
  selectedProductId?: "s" | "m" | "l" | null;
  finalPrice?: number | null;
  contactName?: string | null; // Keep for backward compatibility during hydration
  contactFirstName?: string | null;
  contactLastName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  street?: string | null;
  housenumber?: string | null;
  city?: string | null;
  zipCode?: string | null;
  country?: string | null;
  contactBirthdate?: string | null;
  customerGender?: "male" | "female" | null;
  coverageSelections?: Partial<Record<BaseCoverageId, boolean>>;
  subCoverageSelections?: Partial<Record<BaseCoverageId, Record<string, boolean>>>;
  subCoverageSumInsured?: Partial<Record<BaseCoverageId, Record<string, number>>>;
  coverageDeductibles?: Partial<Record<BaseCoverageId, number>>;
  coverageDeductiblesOtherBand?: Partial<Record<BaseCoverageId, number>>;
  coverageSumInsured?: Partial<Record<BaseCoverageId, number>>;
  coverageSumInsuredPerCase?: Partial<Record<BaseCoverageId, string>>;
  emergencyCareSumInsuredPerDay?: "50" | "100" | null;
  annualDeductible?: number | null;
  dailyTermination?: boolean;
  hasAppliedRecommendation?: boolean;
  hasSeenProductCustomization?: boolean;
  paymentInterval?: 1 | 2 | 4 | 12;
  paymentMethod?: "credit_card" | "ESR";
  startOfInsurance?: string | null;
  firstDue?: string | null;
  isOfferLoadingFromUrl?: boolean;
  dispatchContract?: "customer" | "customer_and_agent" | "agent";
  dispatchInvoice?: "customer" | "customer_and_agent" | "agent";
  dispatchReminder?: "customer" | "customer_and_agent" | "agent";
  dispatchEmail?: "customer" | "customer_and_agent" | "agent";
  agentDispatchEmail?: string | null;
  campaignCode?: string | null;
  applicationAnswers?: { termination?: string | null; owner?: string | null } | null;
  premiumCorrections?: PremiumCorrection[] | null;
  offerLoadedFromUrl?: boolean;
  hasLeftConfirmationAfterOfferLoad?: boolean;
  consultationProtocolDocumentId?: number | null;
  consultationProtocolFilename?: string | null;
}

export const isLocalStorageAvailable = () => {
  if (typeof window === "undefined") return false;
  try {
    const storage = window.localStorage;
    const testKey = "__funnel_storage_test__";
    storage.setItem(testKey, "1");
    storage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

export const loadFunnelFromStorage = (storageKey: string): SerializedFunnelData | null => {
  if (!isLocalStorageAvailable()) return null;
  const raw = localStorage.getItem(storageKey);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SerializedFunnelData;
  } catch (error) {
    console.warn(`Failed to parse stored funnel data for key '${storageKey}', clearing corrupted entry.`, error);
    localStorage.removeItem(storageKey);
    return null;
  }
};

export const saveFunnelToStorage = (storageKey: string, data: SerializedFunnelData) => {
  if (!isLocalStorageAvailable()) return;
  localStorage.setItem(storageKey, JSON.stringify(data));
};
